'use client';

import {
	BigNum,
	PERCENTAGE_PRECISION,
	PERCENTAGE_PRECISION_EXP,
} from '@velocity-exchange/sdk';
import { useMarketInterestData } from 'src/hooks/earn/useMarketInterestData';
import { cm } from 'src/utils/classNames';
import { getMarketIconSrc } from 'src/components/Utils/MarketIcon';
import { useTheme } from 'next-themes';
import { DriftTheme } from 'src/environmentVariables/EnvironmentVariables';
import useIsMobileScreenSize from 'src/hooks/useIsMobileScreenSize';
import useModal from 'src/components/Modals/hooks/useModal';

export interface AssetApyBannerProps {
	marketIndex: number;
	assetLabel: string;
	baseSymbolForIcon: string;
	bodyLabel?: string;
}

const AssetBackgrounds: Record<string, string> = {
	USDC: 'bg-main-bg',
	SOL: 'bg-main-bg bg-[linear-gradient(0deg,var(--positive-green-secondary-bg)_0%,var(--positive-green-secondary-bg)_100%)]',
	wETH: 'bg-main-bg bg-[linear-gradient(0deg,var(--button-selector)_0%,var(--button-selector)_100%)]',
	wBTC: 'bg-main-bg bg-[linear-gradient(0deg,var(--star-yellow-bg)_0%,var(--star-yellow-bg)_100%)]',
	cbBTC:
		'bg-main-bg bg-[linear-gradient(0deg,var(--purple-bg)_0%,var(--purple-bg)_100%)]',
};

function AssetApyBanner({
	marketIndex,
	assetLabel,
	baseSymbolForIcon,
	bodyLabel,
}: AssetApyBannerProps) {
	const { show } = useModal();
	const theme = (useTheme().theme as DriftTheme) ?? DriftTheme.dark;
	const isMobileScreenSize = useIsMobileScreenSize();

	const marketData = useMarketInterestData({ marketIndex });
	if (!marketData) return null;

	const totalApy = marketData.deposit.totalApy;
	const formattedApy = BigNum.from(
		totalApy * PERCENTAGE_PRECISION.toNumber(),
		PERCENTAGE_PRECISION_EXP
	).toFixed(2);

	const handleBannerClick = () => {
		show({
			type: 'deposit',
			props: {
				collateralIndex: marketIndex,
			},
		});
	};

	const iconSrc = getMarketIconSrc({
		baseSymbol: baseSymbolForIcon,
		theme,
		grey: false,
	});

	return (
		<button
			onClick={handleBannerClick}
			className={cm(
				'relative w-full overflow-hidden rounded-sm border border-stroke-divider text-left',
				'transition-colors hover:border-purple-link',
				isMobileScreenSize ? 'p-sm' : 'p-md',
				AssetBackgrounds[assetLabel] ?? 'bg-main-bg'
			)}
		>
			<div
				className={cm(
					'absolute -right-[15px] h-[60px] w-[60px] opacity-40',
					isMobileScreenSize
						? 'translate-x-1/5 -translate-y-1/6 rotate-30'
						: 'top-md -translate-y-1/2 rotate-30'
				)}
			>
				<img
					src={iconSrc}
					alt={assetLabel}
					className="h-full w-full object-contain"
				/>
			</div>

			<div className="relative z-10 flex flex-col gap-y-2xs text-text-default sm:gap-y-sm">
				<span className="mt-0 typo-label-xs sm:typo-label-md">
					{assetLabel}{' '}
					<span className="text-text-positive-green-button">
						{formattedApy}% APY
					</span>
				</span>

				<div className="typo-body-xs">
					<div>
						<span className="block sm:hidden">
							Earn with or without trading.
						</span>
						<span className="hidden sm:block">
							Earn APY on {bodyLabel ?? assetLabel} with or without trading.
						</span>
						<span className="hidden typo-label-xs text-text-positive-green-button sm:block">
							Deposit now!
						</span>
					</div>
				</div>
			</div>
		</button>
	);
}

export default AssetApyBanner;
